import React, { useCallback, useEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { AvisoIa, EquipoMensaje } from "../../../services/v2/prospects.service";
import WappIcono from "../../../components/wappIcono";
import TextoRico from "./textoRico";
import { MedioMensaje } from "./equipoMedios";
import { diaEtiqueta, errorTexto, hora, tonoAvatar } from "./salesHelpers";
import { push } from "./push";

/**
 * Avisos de IA (Esteban, 2026-10-09: "quiero poder ver como superadmin exactamente todo lo que está mandando por chat
 * IA Washington"). Solo Super Admin (el back responde 403 a cualquier otra persona). Lo más nuevo arriba, al instante,
 * con destinataria, fecha, texto completo, archivo y origen; filtros por persona, texto, fecha y origen. Abrir la
 * conversación la deja en modo lectura y no la marca como leída para la destinataria.
 *
 * 2026-10-10 (Esteban: "texto uno encima de otro… no se ven claros"): lista limpia como la de WappTeam, filtros en una
 * fila que se acomoda sola, ligas cortas y las ligas de Zoho CRM como el cliente de WAPP (las cambia el back).
 */
const ORIGEN: { [k: string]: { corto: string; largo: string } } = {
	router: { corto: "Router", largo: "Router de WhatsApp" },
	wapp: { corto: "WAPP", largo: "Avisos de WAPP" },
	cliq: { corto: "Cliq", largo: "Historial de Cliq" },
};

const inicialesDe = (nombre: string): string => {
	const w = nombre.split(/\s+/).filter((x) => /\p{L}/u.test(x[0] || ""));
	return ((w[0]?.[0] || "") + (w[1]?.[0] || "")).toUpperCase() || "?";
};

const AvisosIaView: React.FC<{ campusId: number; onAbrir: (canalId: number) => void; onCerrar: () => void }> = ({ campusId, onAbrir, onCerrar }) => {
	const http = HttpManager.getInstance();
	const [avisos, setAvisos] = useState<AvisoIa[]>([]);
	const [personas, setPersonas] = useState<{ id: number; nombre: string }[]>([]);
	const [hayMas, setHayMas] = useState<boolean>(false);
	const [pagina, setPagina] = useState<number>(1);
	const [persona, setPersona] = useState<number | null>(null);
	const [q, setQ] = useState<string>("");
	const [origen, setOrigen] = useState<string>("");
	const [desde, setDesde] = useState<string>("");
	const [hasta, setHasta] = useState<string>("");
	const [cargando, setCargando] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const filtros = useRef({ persona, q, origen, desde, hasta });
	filtros.current = { persona, q, origen, desde, hasta };
	const hayFiltros = !!(persona || q || origen || desde || hasta);

	const cargar = useCallback(async (page: number) => {
		setCargando(true);
		try {
			const r = await http.prospectsService.equipoAvisosIa(campusId, { ...filtros.current, page });
			setAvisos((prev) => (page === 1 ? r.data.avisos : [...prev, ...r.data.avisos]));
			setHayMas(r.data.hayMas); setPagina(page); setError(null);
			if (page === 1 && r.data.personas.length) setPersonas(r.data.personas);
		} catch (e) { setError(errorTexto(e)); }
		finally { setCargando(false); }
	}, [campusId]);

	useEffect(() => { const t = setTimeout(() => cargar(1), q ? 350 : 0); return () => clearTimeout(t); }, [persona, q, origen, desde, hasta, cargar]);
	// Al instante: cada aviso nuevo de IA recarga la primera página (si no se está viendo una página más abajo).
	useEffect(() => push.on("avisos_ia", () => { if (pagina === 1) cargar(1); }), [pagina, cargar]);

	const limpiar = () => { setPersona(null); setQ(""); setOrigen(""); setDesde(""); setHasta(""); };

	let ultimoDia = "";
	return (
		<div className="ventas-cliente cv aia">
			<div className="cli-cab wchat-cab">
				<div className="cab-fila">
					<button className="cv-volver" onClick={onCerrar} aria-label="Volver a WappTeam">← <span className="txt">WAPPTEAM</span></button>
					<div className="quien">
						<span className="nombre"><WappIcono n="megaphone" /> Avisos de IA</span>
					</div>
				</div>
			</div>
			<div className="aia-filtros" role="search">
				<div className="aia-campo aia-buscar">
					<WappIcono n="search" />
					<input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en los avisos" aria-label="Buscar en los avisos" />
				</div>
				<div className="aia-campo aia-sel">
					<select value={persona || ""} onChange={(e) => setPersona(e.target.value ? Number(e.target.value) : null)} aria-label="Persona">
						<option value="">Todas las Personas</option>
						{personas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
					</select>
				</div>
				<div className="aia-campo aia-sel">
					<select value={origen} onChange={(e) => setOrigen(e.target.value)} aria-label="Origen">
						<option value="">Todos los Orígenes</option>
						<option value="router">Router de WhatsApp</option>
						<option value="wapp">Avisos de WAPP</option>
						<option value="cliq">Historial de Cliq</option>
					</select>
				</div>
				<div className="aia-campo aia-fecha">
					<span>Desde</span>
					<input type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)} aria-label="Desde" />
				</div>
				<div className="aia-campo aia-fecha">
					<span>Hasta</span>
					<input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} aria-label="Hasta" />
				</div>
				{hayFiltros ? <button className="aia-limpiar" onClick={limpiar} title="Quitar todos los filtros">Limpiar</button> : null}
			</div>
			<div className="aia-lista">
				{error ? <div className="aviso error" style={{ margin: 12 }}>{error}</div> : null}
				{!cargando && !avisos.length && !error ? <div className="ventas-vacio">No hay avisos con esos filtros.</div> : null}
				{avisos.map((a) => {
					const dia = diaEtiqueta(a.created_at);
					const sep = dia !== ultimoDia ? <div className="aia-dia">{dia}</div> : null;
					ultimoDia = dia;
					const m = { ...a, user_id: 0, author_name: "IA", edited_at: null, deleted_at: null, reply_to_id: null, r_author: null, r_body: null, r_file_tipo: null,
						duracion: null, menciones: [], reacciones: [], p_first_name: null, p_last_name: null, p_phone: null, p_etapa: null, p_status: null, p_curso: null } as EquipoMensaje;
					const nombre = a.para.corto || a.para.nombre;
					const o = a.origen ? ORIGEN[a.origen] : null;
					return (
						<React.Fragment key={a.id}>
							{sep}
							<article className="aia-item">
								<span className={"eq-av " + tonoAvatar(a.para.id)} aria-hidden="true">{inicialesDe(nombre)}</span>
								<div className="aia-cuerpo">
									<div className="aia-cab">
										<span className="aia-para" title={[a.para.nombre, a.para.rol, a.para.plantel].filter(Boolean).join(" · ")}>
											<b>{nombre}</b>
											{a.para.rol || a.para.plantel ? <small>{[a.para.rol, a.para.plantel].filter(Boolean).join(" · ")}</small> : null}
										</span>
										<span className="aia-hora">{hora(a.created_at)}</span>
									</div>
									{a.file_tipo ? <div className="aia-medio"><MedioMensaje campusId={campusId} m={m} todos={[m]} /></div> : null}
									{a.body ? <TextoRico t={a.body} cortas className="aia-texto" /> : null}
									<div className="aia-pie">
										{o ? <span className="aia-chip" title={o.largo}>{o.corto}</span> : null}
										<span className={"aia-estado" + (a.leido ? "" : " nuevo")}>{a.leido ? <><WappIcono n="check" /> Leído</> : <><i aria-hidden="true" /> Sin Leer</>}</span>
										<button className="aia-abrir" onClick={() => onAbrir(a.canal_id)} title="Abrir la conversación en modo lectura">
											<WappIcono n="message" /> Ver Conversación
										</button>
									</div>
								</div>
							</article>
						</React.Fragment>
					);
				})}
				{hayMas ? <button className="btn-wa aia-mas" disabled={cargando} onClick={() => cargar(pagina + 1)}>{cargando ? "Cargando…" : "Ver Más"}</button> : null}
				{cargando && !avisos.length ? <div className="ventas-vacio">Cargando…</div> : null}
			</div>
		</div>
	);
};

export default AvisosIaView;
